/**
 * The project document: what the user edits and what gets saved. Plain data only.
 * Engine inputs are taken straight from this; results are always recomputed, never stored here.
 * Units are SI and carried in the field names (see docs/build-plan.md §4.7).
 */

export type Id = string;
/** Metres, right-handed, Z up. */
export type Vec3 = [number, number, number];

export type Grade = 'continuous' | 'primary' | 'secondary';
export type ReleaseForm = 'jet' | 'diffusive' | 'heavy';
export type AvailabilityLevel = 'good' | 'fair' | 'poor';
export type Dilution = 'high' | 'medium' | 'low';
export type ZoneNumber = 0 | 1 | 2;
export type GasGroup = 'IIA' | 'IIB' | 'IIC';
export type TemperatureClass = 'T1' | 'T2' | 'T3' | 'T4' | 'T5' | 'T6';
/**
 * Zone shapes, from the suggested shapes of Annex A.2 (Figs A.2–A.5). The engine suggests one from the
 * release; the engineer can change it (MD-018).
 */
export type ShapeKind = 'sphere' | 'vertical-cylinder' | 'column' | 'drip-column' | 'pool-disc';

/**
 * A value the engineer reuses: a hole cross-section, an outdoor ventilation velocity. Saved once and
 * offered next to the matching field. The engineer types these in from their own copy of the standard
 * or their firm's practice; Extent ships none (see docs/standard-content-policy.md).
 */
export interface ReferenceValue {
  id: Id;
  kind: 'hole-area' | 'ventilation-velocity';
  /** What it is for, in the engineer's words, e.g. "Flange, fibre gasket". */
  label: string;
  /** SI: m² for a hole area, m/s for a velocity. */
  valueSi: number;
}

export interface ProjectSettings {
  /** Ambient temperature for gas density, K. 20 °C = 293.15 K. */
  ambientTemperatureK: number;
  /** Ambient (atmospheric) pressure, Pa absolute. */
  ambientPressurePa: number;
  /** Xcrit as a fraction of LFL (C.3.5, C.3.6.2). Default 0.25. */
  criticalConcentrationFraction: number;
  /** Factor applied to LFL in the release characteristic, ≤ 1 (MD-007). Default 1. */
  lflSafetyFactor: number;
  /** The engineer's own reusable values. Empty until they save some. */
  referenceValues?: ReferenceValue[];
}

export interface Substance {
  id: Id;
  name: string;
  casNumber?: string;
  state: 'gas' | 'liquid' | 'liquefied-gas';
  molarMassKgPerKmol: number;
  lflVolFrac: number;
  uflVolFrac?: number;
  aitC?: number;
  flashPointC?: number;
  boilingPointC?: number;
  relativeVapourDensity?: number;
  /** Ratio of specific heats. If absent and cp is given, derived from cp (B.7.2.3.1). */
  gamma?: number;
  cpJPerKgK?: number;
  liquidDensityKgM3?: number;
  vapourPressure?: { pa: number; atTemperatureK: number };
  gasGroup?: GasGroup;
  /** Where each value came from, e.g. { lflVolFrac: 'SDS rev 4, §9' } — clause 10.1 requires sources. */
  dataSources?: Partial<Record<string, string>>;
}

export interface Box {
  center: Vec3;
  size: Vec3;
  /** Rotation about Z, radians. */
  yawRad: number;
}

export interface IndoorVentilation {
  kind: 'indoor';
  type: 'artificial-general' | 'artificial-local' | 'natural';
  /** Fresh air flow through the room, m³/s. */
  airFlowM3PerS: number;
  airFlowBasis?: string;
  /** Direction the air moves through the room; sets the cross-section (C.3.4). */
  flowDirection: Vec3;
  /** ≥ 1. Divides the average velocity for inefficient or obstructed flow (C.3.4). */
  velocityReductionFactor: number;
  /** Ventilation inefficiency f, 1–5 (C.3.6.2). */
  inefficiencyF: number;
}

export interface OutdoorVentilation {
  kind: 'outdoor';
  /** Ventilation velocity exceeded 95 % of the time, m/s (C.3.4). */
  velocityMPerS: number;
  velocityBasis?: string;
  /** Obstructed areas and pits are treated like enclosed spaces for dilution (C.3.5). */
  obstructed: boolean;
}

export interface Area {
  id: Id;
  name: string;
  kind: 'indoor' | 'outdoor';
  /** Indoor: the room volume. Outdoor: optional bounding region. */
  box?: Box;
  ventilation: IndoorVentilation | OutdoorVentilation;
  availability: { level: AvailabilityLevel; basis?: string };
  /** Summation of releases (B.4), indoor areas only. */
  summation?: { maxSimultaneousPrimary: number | 'all'; basis?: string };
}

export type ReleaseInput =
  | {
      model: 'gas';
      pressurePaAbs: number;
      temperatureK: number;
      holeAreaM2: number;
      cd: number;
      z: number;
    }
  | {
      model: 'liquid';
      pressureDiffPa: number;
      holeAreaM2: number;
      cd: number;
      /** Fraction of the liquid that becomes vapour at the source, 0–1 (B.7.2.2 gives no method). */
      vapourFraction: number;
      vapourFractionBasis?: string;
    }
  | {
      model: 'pool';
      areaM2: number;
      liquidTemperatureK: number;
      /** Only if the pool sees a different wind than the dilution assessment (B.7.3 says keep them equal). */
      windSpeedOverrideMPerS?: number;
    }
  | {
      model: 'manual';
      massRateKgPerS: number;
      reference: string;
    };

export interface Override<T> {
  value: T;
  justification: string;
}

export interface Source {
  id: Id;
  tag: string;
  description: string;
  areaId: Id;
  substanceId: Id;
  position: Vec3;
  /** Release direction for directional shapes (A.3). */
  direction?: Vec3;
  grade: Grade;
  gradeBasis?: string;
  release: ReleaseInput;
  releaseForm: ReleaseForm;
  /** D.3: the jet curve needs an explicit "unimpeded" confirmation. */
  releaseFormConfirmed?: boolean;
  /** Override of the suggested zone shape. */
  shape?: ShapeKind;
  /** Answers to judgement prompts, keyed by prompt id. */
  judgements?: Record<string, string>;
  overrides?: {
    dilution?: Override<Dilution>;
    extentHorizontalM?: Override<number>;
    extentVerticalM?: Override<number>;
    outerExtentM?: Override<number>;
  };
  notes?: string;
}

export interface ExMarking {
  epl: 'Ga' | 'Gb' | 'Gc';
  gasGroup: 'II' | GasGroup;
  tClass?: TemperatureClass;
  maxSurfaceTempC?: number;
  marking?: string;
}

export interface Equipment {
  id: Id;
  tag: string;
  description: string;
  areaId?: Id;
  box: Box;
  ex: { kind: 'non-ex' } | ({ kind: 'ex' } & ExMarking);
}

/** Plant geometry for context: vessels, skids, walls. Does not affect the calculation. */
export type SceneObject =
  | { id: Id; name: string; kind: 'box'; box: Box }
  | { id: Id; name: string; kind: 'cylinder'; base: Vec3; radiusM: number; heightM: number };

/** A site plan image laid on the ground for modelling over (plan §5.2). */
export interface Underlay {
  id: Id;
  name: string;
  /** Image as a data URL, downscaled on import. */
  image: string;
  pixelWidth: number;
  pixelHeight: number;
  /** Centre of the image on the ground, metres. */
  center: [number, number];
  /** Real-world width of the whole image, metres. Sets the scale. */
  widthM: number;
  rotationRad: number;
  opacity: number;
  visible: boolean;
}

export interface ProjectDoc {
  schemaVersion: 1;
  meta: { name: string; client?: string; site?: string; reference?: string; notes?: string };
  settings: ProjectSettings;
  substances: Substance[];
  areas: Area[];
  sources: Source[];
  equipment: Equipment[];
  objects: SceneObject[];
  /** Site plan images. Optional: projects saved before underlays existed have none. */
  underlays?: Underlay[];
}

export const DEFAULT_SETTINGS: ProjectSettings = {
  ambientTemperatureK: 293.15,
  ambientPressurePa: 101_325,
  criticalConcentrationFraction: 0.25,
  lflSafetyFactor: 1,
};
